import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";

const positions = [
  {
    title: "潜水士（新卒・第二新卒）",
    type: "正社員",
    location: "本社・全国現場",
    description:
      "国家資格「潜水士」を目指す方を対象とした研修制度あり。未経験者歓迎。体力に自信のある方、海の仕事に興味のある方を求めています。",
    requirements: ["高卒以上", "普通自動車免許", "水泳ができる方"],
    benefits: [
      "潜水士養成研修",
      "国家資格取得支援",
      "寮・社宅あり",
      "各種手当充実",
    ],
  },
  {
    title: "電気通信（新卒・第二新卒）",
    type: "正社員",
    location: "本社・全国現場",
    description:
      "海底ケーブル電気試験・5Gアンテナ取付・住宅電子機器取付・メンテナンスなど、電気通信工事を担当。未経験者歓迎。",
    requirements: ["高卒以上", "普通自動車免許", "電気通信に興味のある方"],
    benefits: [
      "資格取得支援",
      "各種手当充実",
      "寮・社宅あり",
    ],
  },
  {
    title: "技術職（海洋工学科卒）",
    type: "正社員",
    location: "本社",
    description:
      "海洋工学、土木工学、機械工学などの知識を活かし、施工管理・技術開発に従事。現場とオフィスの両方で活躍できます。",
    requirements: ["大学・大学院卒", "理工系学部卒", "CAD操作できる方歓迎"],
    benefits: [
      "資格取得支援",
      "研究開発参加可",
      "フレックスタイム制",
    ],
  },
  {
    title: "事務職（総務・経理）",
    type: "正社員",
    location: "本社",
    description:
      "会社の裏方として、総務・経理・人事業務を担当。潜水業界特有の業務にも慣れていただけます。",
    requirements: ["大卒以上", "PCスキル（Excel・Word）", "コミュニケーション能力"],
    benefits: [
      "業界知識研修",
      "資格取得支援",
      "年間休日120日以上",
      "福利厚生充実",
    ],
  },
];

const benefits = [
  { icon: "ri-book-open-line", title: "充実の研修制度", desc: "入社時基礎研修から現場OJT、年次研修まで段階的にスキルアップ" },
  { icon: "ri-award-line", title: "資格取得支援", desc: "潜水士・施工管理技士など、必要な資格の取得費用を全額支援" },
  { icon: "ri-home-smile-line", title: "住居支援", desc: "独身寮・社宅・住宅手当など、住まいを安心して始められる制度" },
  { icon: "ri-heart-pulse-line", title: "健康診断", desc: "年2回の定期健康診断と、高気圧医学専門医による特別診察" },
  { icon: "ri-calendar-check-line", title: "休暇制度", desc: "年間休日120日以上、有給休暇は入社半年後に10日付与" },
  { icon: "ri-team-line", title: "チームワーク", desc: "先輩後輩の距離が近く、困った時はすぐに相談できる環境" },
];

export default function CareersPage() {
  usePageTitle("採用");
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <section className="relative min-h-[420px] md:min-h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/8e81880eb50dc98f95be220d1124e175/17760651ca32ac59523bc2c9b71f6ed7.jpeg"
              alt="潜水訓練中"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-ocean-900/55" />
          </div>
          <div className="relative z-10 text-center px-4">
            <span className="inline-block px-4 py-1.5 text-xs font-semibold tracking-widest text-ocean-300 border border-ocean-400 rounded-full mb-5">
              RECRUIT
            </span>
            <h1 className="font-serif text-3xl md:text-5xl font-bold text-white mb-4">
              若い力で日本を底上げ
            </h1>
            <p className="text-white/70 text-base md:text-lg">
              未経験者歓迎・充実の研修制度
            </p>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-ocean-50">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10 md:mb-14">
                <span className="inline-block px-4 py-1.5 text-xs font-semibold tracking-widest text-ocean-500 border border-ocean-500 rounded-full mb-4">
                  OPEN POSITIONS
                </span>
                <h2 className="font-serif text-2xl md:text-3xl font-bold text-ocean-800 mb-4">
                  募集要項
                </h2>
                <p className="text-ocean-600 text-sm md:text-base">
                  現在募集中の職種です。ご興味のある方はお気軽にお問い合わせください。
                </p>
              </div>

              <div className="space-y-6">
                {positions.map((position) => (
                  <div
                    key={position.title}
                    className="bg-white rounded-xl p-6 md:p-8 border border-ocean-100 hover:border-ocean-300 hover:shadow-lg transition-all"
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                      <div>
                        <h3 className="text-lg md:text-xl font-bold text-ocean-800 mb-2">
                          {position.title}
                        </h3>
                      </div>
                      <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-ocean-500 hover:bg-ocean-400 text-white text-sm font-medium rounded-full transition-all whitespace-nowrap shrink-0"
                      >
                        応募する
                        <i className="ri-arrow-right-line" />
                      </Link>
                    </div>

                    <p className="text-sm text-ocean-600 leading-relaxed mb-4">
                      {position.description}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-xs font-semibold text-ocean-400 mb-2">
                          応募要件
                        </h4>
                        <ul className="space-y-1">
                          {position.requirements.map((req) => (
                            <li
                              key={req}
                              className="flex items-center gap-2 text-sm text-ocean-700"
                            >
                              <i className="ri-check-line text-ocean-500 text-xs" />
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-ocean-400 mb-2">
                          待遇・福利厚生
                        </h4>
                        <ul className="space-y-1">
                          {position.benefits.map((benefit) => (
                            <li
                              key={benefit}
                              className="flex items-center gap-2 text-sm text-ocean-700"
                            >
                              <i className="ri-check-line text-ocean-500 text-xs" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}